"use client";

import { useQuery } from "@apollo/client/react";
import {
  getCurrentDate,
  type PlainDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";

import { graphql } from "../../../../__generated__/gql";
import type { RoomTypeBrowseQuery } from "../../../../__generated__/gql/graphql";
import { getDefaultReservationLeadMonths } from "../../../../constants";

// 部屋タイプブラウズ用クエリ。$withDeferred=false（構造のみ）と =true（availabilities 込み）を
// マウント時に並列発火し、軽い構造を先に描画して重い価格を後からマージする
// （同一 id に正規化されるためフリッカーしない）。
//
// - fromPrice は FE 側ソートで使うため fragment でマスクせず明示取得
// - firstNight/lastNight 省略時は今日から getDefaultReservationLeadMonths() ヶ月後までを
//   デフォルト範囲とする。BE デフォルト（予約可能期間全体）まで取得すると、先の月の
//   SeasonalFeeSetting が未登録の場合にエラーになるため（インシデント対応）
// - EV 充電器は部屋単位の設備のため roomTypesForReservation と availabilities の両方で絞る
// - nights は日程フィルタ有効時のみ取得（$isDateFilterActive。無しの browse は fromPrice
//   しか使わず、全夜ぶん取るとペイロードが肥大化する）
// - FeatureSubtext_RoomType は RoomTypeRow が直接 FeatureSubtext に渡すためこの階層で spread する
const RoomTypeBrowseQueryDocument = graphql(`
  query RoomTypeBrowse(
    $provider: AccommodationReservationProviderInput!
    $withDeferred: Boolean!
    $isDateFilterActive: Boolean!
    $facilitySlugs: [String!]
    $evChargerAvailable: Boolean
    $architectureTypes: [String!]
    $largeOnly: Boolean
    $firstNight: PlainDate
    $lastNight: PlainDate
  ) {
    areasForReservation(provider: $provider) {
      id
      name
      slug
      sitesForReservation(provider: $provider) {
        id
        name
        slug
        shortAddress
        content {
          id
          prefecture
          city
          heroImages {
            rawUrl
          }
        }
        roomTypesForReservation(
          provider: $provider
          facilitySlugs: $facilitySlugs
          evChargerAvailable: $evChargerAvailable
          architectureTypes: $architectureTypes
          largeOnly: $largeOnly
        ) {
          id
          displayName
          architectureType {
            id
            name
            series
            capacity
            content {
              id
              capacityDescription
            }
          }
          features {
            id
            slug
          }
          ...FeatureSubtext_RoomType
          availabilities(
            firstNight: $firstNight
            lastNight: $lastNight
            provider: $provider
            evChargerAvailable: $evChargerAvailable
          ) @include(if: $withDeferred) {
            fromPrice
            nights @include(if: $isDateFilterActive) {
              night
              availableAccommodations {
                id
              }
            }
          }
        }
      }
    }
  }
`);

export type BrowseArea = RoomTypeBrowseQuery["areasForReservation"][number];
export type BrowseSite = BrowseArea["sitesForReservation"][number];
export type BrowseRoomType = BrowseSite["roomTypesForReservation"][number];

// selectVisibleAreas が付与する「日程フィルタで全泊を1室で通せない」フラグ付きの型。
// 空きなし部屋タイプを一覧から消さず「満室」として表示・末尾ソートするために使う。
export type BrowseRoomTypeWithAvailability = BrowseRoomType & {
  unavailableForRange: boolean;
};
export type BrowseSiteWithAvailability = Omit<
  BrowseSite,
  "roomTypesForReservation"
> & {
  roomTypesForReservation: BrowseRoomTypeWithAvailability[];
};
export type BrowseAreaWithAvailability = Omit<
  BrowseArea,
  "sitesForReservation"
> & {
  sitesForReservation: BrowseSiteWithAvailability[];
};

// 価格表示用。availabilities 未取得（構造フェーズ）は Infinity 扱い。
// 満室（unavailableForRange）の部屋は「通せない日程の価格」で見出しを誤解させないよう最安計算から除外する。
const roomTypeMinPrice = (rt: BrowseRoomTypeWithAvailability) =>
  rt.unavailableForRange
    ? Infinity
    : (rt.availabilities?.fromPrice ?? Infinity);
const siteMinPrice = (s: BrowseSiteWithAvailability) =>
  Math.min(...s.roomTypesForReservation.map(roomTypeMinPrice), Infinity);

/** 拠点の表示用 fromPrice（配下の空きあり部屋タイプの最安。空きあり部屋が無ければ null） */
export function siteFromPrice(site: BrowseSiteWithAvailability): number | null {
  const min = siteMinPrice(site);
  return Number.isFinite(min) ? min : null;
}

export function useRoomTypeSearch({
  stayTicketUnitId,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
  firstNight,
  lastNight,
}: {
  stayTicketUnitId: string | null;
  facilitySlugs: string[];
  evChargerAvailable: boolean;
  architectureTypes: string[];
  largeOnly: boolean;
  firstNight: PlainDate | null;
  lastNight: PlainDate | null;
}) {
  // 両日程が揃っている場合のみ「ユーザーが日程を明示指定した」扱いにする。片方だけのときは
  // フィルタなし（デフォルト範囲）に倒す
  const isDateFilterActive = firstNight !== null && lastNight !== null;

  // 日程未選択時は today 〜 today+DEFAULT ヶ月後をフォールバック範囲として BE に渡す
  const today = getCurrentDate();
  const effectiveFirstNight = toPlainDateString(firstNight ?? today);
  const effectiveLastNight = toPlainDateString(
    lastNight ?? today.add({ months: getDefaultReservationLeadMonths(today) }),
  );

  const baseVariables = {
    provider: {
      providerType: "StayTicket" as const,
      providerStayTicketUnitId: stayTicketUnitId ?? "",
    },
    isDateFilterActive,
    // 空配列・false は BE に「フィルタなし」として伝えるため null に変換する
    facilitySlugs: facilitySlugs.length > 0 ? facilitySlugs : null,
    evChargerAvailable: evChargerAvailable ? true : null,
    architectureTypes: architectureTypes.length > 0 ? architectureTypes : null,
    largeOnly: largeOnly ? true : null,
    firstNight: effectiveFirstNight,
    lastNight: effectiveLastNight,
  };

  // 構造のみ（軽量・先に返る）。日程フィルタ時は full クエリしか使わない（在庫が揃うまで待つ）ため、
  // 構造クエリは無駄になる。skip してリクエストを減らす。
  const { data: structureData, error: structureError } = useQuery(
    RoomTypeBrowseQueryDocument,
    {
      variables: { ...baseVariables, withDeferred: false },
      skip: !stayTicketUnitId || isDateFilterActive,
      fetchPolicy: "cache-first",
    },
  );

  // availabilities 込み（重い・遅れて返る）
  const { data: fullData, error: fullError } = useQuery(
    RoomTypeBrowseQueryDocument,
    {
      variables: { ...baseVariables, withDeferred: true },
      skip: !stayTicketUnitId,
      fetchPolicy: "cache-first",
    },
  );

  // 日程フィルタ時は構造クエリを skip するので full クエリのエラーを見る。
  const error = isDateFilterActive ? fullError : structureError;

  // 日程フィルタ時は在庫（availabilities）が来ないと満室判定・並び順が確定せず、構造だけ
  // 先に描画すると在庫到着で再配置が起きてちらつく。よって在庫が揃うまで待つ。
  // browse-all は従来どおり構造を即描画（並べ替えないのでちらつかない）。
  const data = isDateFilterActive ? fullData : (fullData ?? structureData);
  // 構造取得前は全体スケルトン。価格の読み込み中は各行で個別にスケルトン表示する。
  // 日程フィルタ時は在庫が揃うまで（ちらつき回避のため）全体スケルトンを維持する。
  const loading = isDateFilterActive ? !fullData : !structureData && !fullData;
  // エリア・拠点は BE 順のまま並べ替えない（価格ロード後の並べ替えは above-the-fold が
  // ガタつく）。並べ替えは折りたたみ内の部屋タイプだけで行う。
  // areasForReservation はフィルタに関係なく全エリア・拠点を返す（エリアフィルタチップ用）。
  const allAreas = data?.areasForReservation ?? [];

  return {
    allAreas,
    loading,
    error,
  };
}
